import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { api, imgSrc } from "../lib/api";
import { fallbackImages, defaultHeadline } from "../config/pitch";
import { PitchProvider } from "../context/PitchContext";
import { FullScreenLoader } from "../context/AuthContext";
import { Hero } from "../components/Hero";
import { Marquee } from "../components/Marquee";
import { BeforeAfter } from "../components/BeforeAfter";
import { Proof } from "../components/Proof";
import { Cta } from "../components/Cta";
import { ProductScreen } from "../components/ProductScreen";
import { InspectionPack } from "../components/InspectionPack";
import { LogoMark } from "../components/Logo";

const toPitch = (d) => {
  const vesselName = d?.vesselName ?? "";
  const lines = Array.isArray(d?.headlineLines) && d.headlineLines.length === 3 ? d.headlineLines : defaultHeadline;
  return {
    customerName: d?.customerName ?? "",
    vesselName,
    vesselStory: d?.vesselStory ?? "",
    headlineLines: lines,
    heroImage: imgSrc(d?.heroImage || fallbackImages.heroImage),
    heroImageAlt: `${vesselName} on the river`,
    closingImage: imgSrc(d?.closingImage || fallbackImages.closingImage),
    helmImage: imgSrc(d?.helmImage || fallbackImages.helmImage),
  };
};

const PitchView = ({ pitch }) => {
  useEffect(() => {
    document.title = `MarineOS for ${pitch.customerName} · ${pitch.vesselName}`;
    const lenis = new Lenis({ duration: 1.2, anchors: true });
    let id;
    const raf = (t) => { lenis.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); };
  }, [pitch]);

  return (
    <PitchProvider value={pitch}>
      <main className="font-body antialiased">
        <Hero />
        <ProductScreen />
        <Marquee />
        <BeforeAfter />
        <InspectionPack />
        <Proof />
        <Cta />
      </main>
    </PitchProvider>
  );
};

const Missing = ({ text }) => (
  <div data-testid="pitch-missing" className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#0B192C] px-6 text-center font-body text-[#F4EDE3]">
    <LogoMark className="h-14 w-14" />
    <p className="font-display text-3xl">{text}</p>
    <a href="mailto:marcos@navscora.com" className="text-sm text-[#D4A373] underline-offset-4 hover:underline">marcos@navscora.com</a>
  </div>
);

const markVisit = (slug) => {
  const key = `marineos-visited:${slug}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    // storage unavailable: still record the visit
  }
  api.post(`/pitches/${slug}/visit`).catch(() => null);
};

export default function PitchPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let alive = true;
    setState({ status: "loading" });
    api
      .get(`/pitches/${slug}`)
      .then((r) => {
        if (!alive) return;
        setState({ status: "ready", pitch: toPitch(r.data) });
        markVisit(slug);
      })
      .catch((e) => alive && setState({ status: e?.response?.status === 404 ? "missing" : "error" }));
    return () => { alive = false; };
  }, [slug]);

  if (state.status === "loading") return <FullScreenLoader />;
  if (state.status === "missing") return <Missing text="This page isn't available." />;
  if (state.status === "error") return <Missing text="We couldn't load this page. Please try again shortly." />;
  return <PitchView pitch={state.pitch} />;
}
